import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { AllocAlertItem } from './AllocChangeAlerts';
import { ErrorBox } from './DataTable';

type Hist = Awaited<ReturnType<typeof api.listAllocHistory>>;

/**
 * 운행편성별 변경 이력(10-12 way) — 기준정보 › 운행차량편성 수정 창 아래.
 * ① 편성 변경 기록(이 편성이 옛/새 편성인 것 — 알림 모달과 같은 카드) ② 저장 이력(저장할 때마다 남은 편성 값, 접어 둠).
 */
export default function AllocHistoryPanel({ carAllocId, reloadKey }: { carAllocId: number; reloadKey: number }) {
  const [h, setH] = useState<Hist | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLog, setShowLog] = useState(false);

  useEffect(() => {
    setError(null);
    api.listAllocHistory({ carAllocId }).then(setH).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [carAllocId, reloadKey]);

  if (error) return <ErrorBox message={error} />;
  if (!h) return <p className="text-sm text-ink-faint">변경 이력을 불러오는 중…</p>;

  return (
    <section className="space-y-2 rounded-xl border border-line p-3">
      <div className="flex items-center gap-2">
        <div className="font-semibold">변경 이력</div>
        <span className="text-xs text-ink-soft">편성 변경 {h.changes.length}건 · 저장 이력 {h.log.length}건</span>
      </div>
      {h.changes.length === 0 && <p className="text-sm text-ink-faint">이 편성의 편성 변경 기록이 없습니다.</p>}
      {h.changes.map((r) => (
        <div key={r.alloc_change_id} className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-ink-soft">
            <span>{r.new_alloc_id === carAllocId ? (r.kind === '신규' ? '이 편성이 등록됨' : `NO ${r.old_alloc_id ?? '—'} 에서 이어받음`)
              : r.kind === '변경' ? `NO ${r.new_alloc_id ?? '—'} 로 넘어감` : `이 편성 ${r.kind}`}</span>
            <Link className="ml-auto text-brand-700 underline" to={`/alloc-changes?mon=${r.settle_mon}&id=${r.alloc_change_id}`}>편성 변경에서 보기</Link>
          </div>
          <AllocAlertItem r={r} sales={false} />
        </div>
      ))}
      {h.log.length > 0 && (
        <div>
          <button type="button" className="text-sm text-brand-700 underline" onClick={() => setShowLog((v) => !v)}>
            {showLog ? '저장 이력 접기' : `저장 이력 ${h.log.length}건 펼치기`}
          </button>
          {showLog && (
            <div className="mt-2 max-h-80 overflow-auto rounded-lg border border-line">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="sticky top-0 bg-canvas text-xs text-ink-soft">
                  <tr>{['저장 시각', '저장한 사람', '차량', '기사', '차주', '소속', '편성기간', '정산'].map((t) => <th key={t} className="px-2 py-1 text-left">{t}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {h.log.map((l) => (
                    <tr key={l.car_alloc_log_id}>
                      <td className="whitespace-nowrap px-2 py-1">{l.created_at ?? '—'}</td>
                      <td className="px-2 py-1">{l.created_name ?? '—'}</td>
                      <td className="px-2 py-1">{l.car_no ?? '—'}</td>
                      <td className="px-2 py-1">{l.driver_name ?? '—'}</td>
                      <td className="px-2 py-1">{l.owner_name ?? '—'}</td>
                      <td className="px-2 py-1">{l.grp_name ?? '—'}</td>
                      <td className="whitespace-nowrap px-2 py-1">{l.startdt || '—'} ~ {l.enddt || '무기한'}</td>
                      <td className="px-2 py-1">{l.is_use ? '진행중' : '종료'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
